import axios from 'axios';
import { toast } from 'sonner';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token from localStorage (oidc-client-ts stores it there).
api.interceptors.request.use((config) => {
  const oidcKey = Object.keys(localStorage).find((k) => k.startsWith('oidc.user:'));
  if (oidcKey) {
    try {
      const oidcData = JSON.parse(localStorage.getItem(oidcKey) || '{}');
      if (oidcData.access_token) {
        config.headers.Authorization = `Bearer ${oidcData.access_token}`;
      }
    } catch {
      // ignore
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired — redirect to login.
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      toast.error('Access denied — insufficient permissions');
    } else if (error.response?.status && error.response.status >= 500) {
      toast.error(`Server error (${error.response.status})`);
    } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      toast.error('Request timed out — please try again');
    } else if (!error.response && error.message === 'Network Error') {
      toast.error('Network error — check your connection');
    }
    return Promise.reject(error);
  }
);

// API functions.

export const fetchDashboardSummary = (tenantId?: string) =>
  api.get('/dashboard/summary', { params: { tenant_id: tenantId } }).then((r) => r.data);

export const fetchHosts = (tenantId?: string) => api.get('/hosts', { params: { tenant_id: tenantId } }).then((r) => r.data);

export const fetchHost = (id: number) => api.get(`/hosts/${id}`).then((r) => r.data);

export const fetchTenants = () => api.get('/tenants').then((r) => r.data);

export const fetchAlerts = (params?: { min_level?: number; hours?: number; tenant_id?: string; source?: string; severity?: string }) =>
  api.get('/alerts', { params }).then((r) => r.data);

export const fetchAnalysisResults = (params?: {
  page?: number;
  page_size?: number;
  analyzer?: string;
  tenant_id?: string;
  status?: string;
}) => api.get('/analysis-results', { params }).then((r) => r.data);

export const fetchAnalysisResult = (id: number) =>
  api.get(`/analysis-results/${id}`).then((r) => r.data);

export const fetchAIResults = (params?: {
  page?: number;
  page_size?: number;
  type?: string;
  tenant_id?: string;
}) => api.get('/ai-results', { params }).then((r) => r.data);

export const fetchAIResult = (id: number) =>
  api.get(`/ai-results/${id}`).then((r) => r.data);

export const triggerAI = (data: { tenant_id: string; type?: string }) =>
  api.post('/ai/trigger', data).then((r) => r.data);

export const fetchConfig = () => api.get('/config').then((r) => r.data);

export const fetchSchedulerStatus = () => api.get('/scheduler/status').then((r) => r.data);

export const fetchTrendingMetrics = () => api.get('/trending').then((r) => r.data);

export const fetchTrendingData = (metric: string, params?: { tenant_id?: string; limit?: number }) =>
  api.get(`/trending/${metric}`, { params }).then((r) => r.data);

// Client management API functions.

export const fetchClients = (status?: string) =>
  api.get('/clients', { params: { status } }).then((r) => r.data);

export const fetchClient = (id: string) =>
  api.get(`/clients/${id}`).then((r) => r.data);

export const createClient = (data: {
  name: string;
  industry?: string;
  contact_email?: string;
  services_json?: string;
  users?: { email: string; first_name: string; last_name: string; role: string }[];
}) => api.post('/clients', data).then((r) => r.data);

export const updateClient = (id: string, data: Record<string, any>) =>
  api.patch(`/clients/${id}`, data).then((r) => r.data);

export const deleteClient = (id: string) =>
  api.delete(`/clients/${id}`).then((r) => r.data);

export const fetchClientOnboarding = (id: string) =>
  api.get(`/clients/${id}/onboarding`).then((r) => r.data);

export const onboardClient = (id: string) =>
  api.post(`/clients/${id}/onboard`).then((r) => r.data);

// Analyzer manual trigger API functions.

export const fetchAnalyzers = () =>
  api.get('/analyzers').then((r) => r.data);

export const runAnalyzer = (name: string, tenantId?: string) =>
  api.post(`/analyzers/${name}/run`, null, { params: { tenant_id: tenantId } }).then((r) => r.data);

export const runAllAnalyzers = () =>
  api.post('/analyzers/run-all').then((r) => r.data);

// Installer download (fetches with auth header, then triggers browser download).

export const downloadInstaller = async (clientId: string, platform: string) => {
  const response = await api.get(`/clients/${clientId}/installer/${platform}`, {
    responseType: 'blob',
    timeout: 120000,
  });
  const ext = platform === 'windows' ? '.exe' : '';
  const filename = `secuguard-installer-${clientId}-${platform}${ext}`;
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Per-user deployment script download.

export const downloadUserDeployScript = async (clientId: string, userId: number) => {
  const response = await api.get(`/clients/${clientId}/users/${userId}/deploy-script`, {
    responseType: 'blob',
    timeout: 30000,
  });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename=(.+)/);
  const filename = match ? match[1] : `Setup-${userId}.ps1`;
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Remediation API functions.

export const fetchRemediations = (params?: {
  page?: number;
  page_size?: number;
  status?: string;
  severity?: string;
  hostname?: string;
  finding_type?: string;
  tenant_id?: string;
}) => api.get('/remediations', { params }).then((r) => r.data);

export const fetchRemediation = (id: string) =>
  api.get(`/remediations/${id}`).then((r) => r.data);

export const approveRemediation = (id: string) =>
  api.post(`/remediations/${id}/approve`).then((r) => r.data);

export const rejectRemediation = (id: string) =>
  api.post(`/remediations/${id}/reject`).then((r) => r.data);

export const deployRemediation = (id: string) =>
  api.post(`/remediations/${id}/deploy`).then((r) => r.data);

export const triggerRemediationScan = () =>
  api.post('/remediations/scan').then((r) => r.data);

export const fetchRemediationStats = (tenantId?: string) =>
  api.get('/remediations/stats', { params: { tenant_id: tenantId } }).then((r) => r.data);

export default api;
